import { Location } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import * as FileSaver from 'file-saver';
import {
  AddCompanyRequest, AddCompanyResponse,
  CompanyType,
  GetCompanyByIdResponse,
  ICompany, IdentityWebApiClient,
  ListUsersRequest,
  ListUsersResponse,
  UpdateCompanyRequest, UpdateCompanyResponse
} from 'identity-webapi-client';
import { Observable } from 'rxjs-compat';
import { map } from 'rxjs/operators';
import { Company, CompanySubjectPair, FileVaultCategory, FileVaultReferenceRelation, FileVaultSubjectType, FileVaultUploadCommitRequest, FileVaultUploadRequest, GetFileVaultReferenceRelationsRequest, GetManufacturerRequest, GetManufacturerResponse, ListHomeDetailsRequest, ListHomeDetailsResponse, RemoveFileVaultReferencesRequest, SituViewWebApiClient } from 'situview-webapi-client';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';
import { AuthService, BreadcrumbService, CompanyService, CompanyViewService, ConfigService, ConfirmDialogService, PaginatedProducts, ProductService, StateService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';

@Component({
  selector: 'companies-add-edit',
  templateUrl: './companies-add-edit.component.html',
  styleUrls: ['./companies-add-edit.component.scss']
})

export class CompaniesAddEditComponent extends SituBaseComponent {
  manufacturerForm: FormGroup;
  submitted = false;
  isAddMode: boolean = false;
  id: string = '';
  company: string = '';
  companyEntity: ICompany | undefined;
  editCollapsed: boolean = true;
  companyTypes = CompanyType;
  situViewUrl: string;
  marketingPanels: FileVaultReferenceRelation[] = [];

  readonly imagetypes = ['image/jpeg', 'image/webp', 'image/svg+xml', 'image/png', 'image/gif', 'image/avif', 'image/apng'];
  readonly acceptedTypes = ['.jpg', '.jpeg', '.webp', '.svg', '.png', '.gif', '.avif', '.apng', '.mp4'];

  constructor(
    authService: AuthService,
    stateService: StateService,
    private route: ActivatedRoute,
    private router: Router,
    private situViewClient: SituViewWebApiClient,
    private formBuilder: FormBuilder,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private identityApiClient: IdentityWebApiClient,
    private location: Location,
    private companyViewService: CompanyViewService,
    private companyService: CompanyService,
    private confirmDialogService: ConfirmDialogService,
    private configService: ConfigService,
    private productService: ProductService) {
    super(authService, stateService);
    this.situViewUrl = configService.config.situViewWebApiBaseUrl;
    this.manufacturerForm = this.formBuilder.group({
      id: [],
      name: [null, Validators.required],
      parentCompanyId: [],
      code: [],
      locale: [],
      timeZone: [],
      type: [0, null],
      status: [],
      postalAddress: [],
      streetAddress: [],
      website: [],
      logoUrl: []
    });
    this.loadCompanies();
    this._subscriptions = [];
    this.id = this.route.snapshot.params['companyId'];
    this.isAddMode = !this.id;
    this.displayCompanyName = this.displayCompanyName.bind(this);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.filteredCompanies = this.manufacturerForm.controls.parentCompanyId.valueChanges.pipe(
      map((value) => {
        return this.filterCompanies(value);
      })
    );

    if (!this.isAddMode) {
      if (this.id !== this.selectedCompany?.id) {
        if (!this.authService.isAuthorizedForCompanyById(this.id)) {
          this.showMessage(`Sorry! Your are not authorized for company ${this.id}.`);
          this.router.navigateByUrl('/unauthorized');
          return;
        }

        this.companyService.getCompanyById(this.id)
          .then((company) => {
            if (!company) return;

            this.stateService.state = {
              ...this.stateService.state,
              company
            };
          });
      }
      this.loadCompany();
      this.loadUserCount();
      this.loadHomeCount();
      this.loadProductCount();
      this.loadMarketingFile();

    } else {
      this.manufacturerForm.removeControl("id");
    }

    this.manufacturerForm.get("name")?.valueChanges.subscribe(x => {
      this.breadcrumbService.setParam('companyName', x);
    });
  }

  get form() { return this.manufacturerForm.controls; }

  onSubmit() {
    this.submitted = true;

    if (this.manufacturerForm.invalid) {
      this.submitted = false;
      return;
    }

    if (this.isAddMode) {
      this.addCompany();
    } else {
      this.updateCompany();
    }
  }

  getCompanyLogo(companyId: string) {
    return this.allCompanies.find(x => x.id === companyId)?.logoUrl;
  }

  getCompanyName(companyId: string) {
    return this.allCompanies.find(x => x.id === companyId)?.name;
  }

  displayCompanyName(companyId: string): string {
    return this.getCompanyName(companyId) ?? companyId;
  }

  /*  Company AutoComplete Filter */
  companyControl = new FormControl();
  filteredCompanies: Observable<Company[]> | undefined;
  allCompanies: Company[] = [];

  private loadCompanies() {
    return this.companyViewService.LoadCompanies()
      .then((companies: Company[]) => {
        this.allCompanies = companies;
      });
  }

  private filterCompanies(searchTerm: string | undefined = undefined): Company[] {
    var companies = this.allCompanies;

    // Filter Company Names by the supplied Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      companies = companies.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(searchTerm!));
    }

    return companies;
  }
  /*  Company AutoComplete Filter */

  updateCompany() {
    let request = this.manufacturerForm.value as UpdateCompanyRequest;
    if (!request.parentCompanyId) request.parentCompanyId = undefined;

    this.identityApiClient.updateCompany(request)
      .then((x: UpdateCompanyResponse) => {
        if (x.success) {
          this.snackBar.open(`${x.company?.name} updated!`, 'OK', { duration: 2000 });
        } else {
          this.snackBar.open(`Error: ${x.errorMessages}!`, 'OK', { duration: 2000 });
        }
      })
      .catch((err: any) => {
        this.snackBar.open(`There was an error updating ${request.name}!`, 'OK', { duration: 2000 });
      });
  }

  addCompany() {
    let request = this.manufacturerForm.value as AddCompanyRequest;
    if (!request.parentCompanyId) request.parentCompanyId = undefined;

    this.identityApiClient.addCompany(request)
      .then((x: AddCompanyResponse) => {
        if (x.success && x.company) {
          this.snackBar.open(`${x.company.name} added!`, 'OK', { duration: 2000 });
          this.manufacturerForm.addControl("id", new FormControl(''));
          this.manufacturerForm.patchValue(x.company);
          this.manufacturerForm.controls.code.disable();
          this.isAddMode = false;
          this.updateUrl(x?.company?.id);
        } else {
          this.snackBar.open(`Error: ${x.errorMessages}!`, 'OK', { duration: 2000 });
        }
      })
      .catch((err: any) => {
        this.snackBar.open(`There was an error adding ${request.name}!`, 'OK', { duration: 2000 });
      });
  }

  updateUrl(newCompanyId: string | undefined) {
    const currentPath = this.location.path();
    const newPath = currentPath.replace('/add', `/${newCompanyId}`);
    this.location.replaceState(newPath);
  }

  resetManufacturer() {
    if (this.isAddMode) {
      this.manufacturerForm.reset();
    } else {
      this.manufacturerForm.reset();
      this.loadCompany();
    }
  }

  showManufacturersView() {
    this.router.navigate(['/admin/companies']);
  }

  getDetailTitleText(): string {
    return this.isAddMode ? "Add" : `${this.manufacturerForm?.get("name")?.value}`;
  }

  getSubmitButtonText(): string {
    return this.isAddMode ? "Add" : "Update";
  }


  getFileDownloadUrl(file: FileVaultReferenceRelation) {
    if (file) {
      var url = `${this.situViewUrl}/api/commands/experimental/FileVault/GetFile?companyId=${file.companyId}&fileVaultReferenceId=${file.id}`;
      return url;
    }
    return '';
  }

  async loadMarketingFile() {

    var request = new GetFileVaultReferenceRelationsRequest();
    request.companyId = this.id;
    request.subjectId = this.id;
    request.category = FileVaultCategory.MarketingMaterial;

    this.situViewClient.getFileVaultReferenceRelations(request).then(response => {
      if (!response.success) {
        this.showMessage("Failed to load marketing file");
        return;
      }
      this.marketingPanels = response.fileVaultReferenceRelations || [];
    });
  }

  async download(file: FileVaultReferenceRelation) {

    var fileResponse = await this.situViewClient.getFile(file.companyId, file.id);

    if (fileResponse.status !== 200) {
      this.showMessage("Failed to load download file");
      return;
    }
    FileSaver.saveAs(fileResponse.data, file.fileName);
  }

  async onFileChosen(event: any) {
    ([...event.target.files] as File[]).forEach(file => this.uploadBlob(file));
  }

  async uploadBlob(fileDetails: File) {

    try {
      const fileVaultUploadRequest = new FileVaultUploadRequest();
      fileVaultUploadRequest.companyId = this.selectedCompany?.id;
      fileVaultUploadRequest.subjectId = this.selectedCompany?.id;
      fileVaultUploadRequest.subjectType = FileVaultSubjectType.Company;
      fileVaultUploadRequest.fileName = fileDetails.name;
      fileVaultUploadRequest.category = FileVaultCategory.MarketingMaterial;

      const requestUploadResponse = await this.situViewClient.requestFileVaultUpload(fileVaultUploadRequest);

      if (!requestUploadResponse.success) {
        let msg = requestUploadResponse.errorMessages?.find(_ => true);
        if (msg) {
          msg = `Failed to request file upload: ${msg}`;
        } else {
          msg = "Failed to request file upload.";
        }

        throw new Error(msg);
      }

      const uploadResponse = await fetch(requestUploadResponse.uploadUrl!, {
        method: 'PUT',
        headers: {
          'Content-Type': `${fileDetails.type}`,
          'x-ms-blob-content-type': `${fileDetails.type}`,
          'x-ms-blob-type': 'BlockBlob'
        },
        body: fileDetails,
      });

      if (!uploadResponse.ok) {
        // Upload completed
        throw new Error("Failed to upload file.");
      }

      const fileVaultUploadCommitRequest = new FileVaultUploadCommitRequest();
      fileVaultUploadCommitRequest.companyId = this.selectedCompany?.id;
      fileVaultUploadCommitRequest.fileVaultReferenceId = requestUploadResponse.fileVaultReferenceId;
      const uploadCommitResponse = await this.situViewClient.commitFileVaultUpload(fileVaultUploadCommitRequest);

      if (!uploadCommitResponse.success) {
        // Upload completed
        throw new Error("Failed to commit file.");
      }

      const existingFile = uploadCommitResponse.fileVaultReferenceRelation;
      if (existingFile) {
        this.marketingPanels.push(existingFile);
      }

    } catch (e: any) {
      this.showMessage(e.message);
    }

  }

  removeFile(file: FileVaultReferenceRelation) {

    this.confirmDialogService.open({
      title: 'Remove File',
      message: `Are you sure you wish to remove '${file.fileName}' permanently?`,
      cancelText: 'No',
      confirmText: 'Yes',
      actionIcon: 'delete',
      actionColour: 'warn'
    } as ConfirmDialogData);

    this.confirmDialogService.confirmed().subscribe(async confirmed => {
      if (confirmed) {
        await this.removeFileRequest(file);
      }
    });
  }

  async removeFileRequest(file: FileVaultReferenceRelation) {
    var removeReferencesRequest = new RemoveFileVaultReferencesRequest();
    removeReferencesRequest.fileVaultReferenceId = file.id;
    removeReferencesRequest.category = FileVaultCategory.MarketingMaterial;
    removeReferencesRequest.force = true;

    var companySubjectPair = new CompanySubjectPair;
    companySubjectPair.companyId = this.selectedCompany?.id;
    companySubjectPair.subjectId = this.selectedCompany?.id

    removeReferencesRequest.subjects = [companySubjectPair];
    var removeReferenceResponse = await this.situViewClient.removeFileVaultReferences(removeReferencesRequest);

    if (!removeReferenceResponse.success) {

      this.showMessage("Failed to remove file from company");
    }
    else {
      this.showMessage(`Removed file from company`);
      this.loadMarketingFile();
    }
  }

  private loadCompany(): void {
    this.identityApiClient.getCompanyById(this.id)
      .then((x: GetCompanyByIdResponse) => {
        if (x.success && x.company) {
          this.companyEntity = x.company;
          if (!this.companyEntity.type) this.companyEntity.type = 0;
          this.manufacturerForm.patchValue(x.company);
          this.loadManufacturer();
          this.manufacturerForm.controls.code.disable();
        } else {
          this.snackBar.open(`Could not fetch the company. Refresh Page.`, 'OK', { duration: 2000 });
        }
      });
  }

  private loadManufacturer() {
    let getManufacturerRequest = new GetManufacturerRequest();
    getManufacturerRequest.manufacturerId = this.id;
    this.situViewClient.getManufacturer(getManufacturerRequest)
      .then((x: GetManufacturerResponse) => {
        if (x.success && x.manufacturer) {
          this.manufacturerForm.controls.logoUrl.patchValue(x.manufacturer?.logoUrl);
        } else {
          this.snackBar.open(`Could not fetch the company. Refresh Page.`, 'OK', { duration: 2000 });
        }
      });
  }

  userCount: number = 0;
  private loadUserCount() {
    let request = new ListUsersRequest();
    request.companyId = this.id;
    request.pageNumber = 1;
    request.pageSize = 1;

    this.identityApiClient.listUsers(request)
      .then((response: ListUsersResponse) => {
        if (response.success && response.totalItems) {
          this.userCount = response.totalItems;
        }
      });
  }

  homeCount: number = 0;
  private loadHomeCount() {
    let request = new ListHomeDetailsRequest();
    request.companyId = this.id;
    request.excludeHomesWithNoTags = false;
    request.pageNumber = 1;
    request.pageSize = 1;

    this.situViewClient.listHomeDetails(request)
      .then((response: ListHomeDetailsResponse) => {
        if (response.success && response.totalItems) {
          this.homeCount = response.totalItems;
        }
      })
      .catch((err: any) => { });
  }

  productCount: number = 0;
  private loadProductCount() {
    this.productService.LoadPaginatedProducts(
      this.id,
      undefined,
      this.id,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined, //ranges
      undefined,
      undefined,
      false,
      false, //ranges
      false,
      undefined,
      undefined,
      1,
      1
    ).then((response: PaginatedProducts) => {
      if (response?.totalItems) {
        this.productCount = response.totalItems;
      }
    });
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}
